import { priceFormatter } from '@2wunder/klarx-tool'
import { Space } from 'antd'
import { ColumnProps } from 'antd/lib/table'
import KEmpty from 'app/components/Modules/General/KEmpty'
import KTable from 'app/components/Modules/Table/KTable'
import { RecordType, TableProps } from 'app/constants/GlobalTypes'
import {
  invoiceMonthNormalizer,
  QlGroupedInvoicesResponseType
} from 'app/lib/apollo/normalizers/Invoice'
import { useDate } from 'app/lib/hooks/toolBox'
import useFilterParams from 'app/lib/hooks/useFilterParams'
import { useQuery } from 'app/lib/hooks/useQuery'
import useTranslation from 'app/lib/hooks/useTranslation'
import { INVOICES_GROUPED_BY_MONTH } from 'app/queries/Invoice'
import { omitBy } from 'lodash'
import * as React from 'react'
import { useEffect, useState } from 'react'

import ExportButton from '../ExportButton'
import InvoiceModal from '../Modal/Modal'
import Status from '../Status'
import InvoiceAction from './Cells/Actions'
import EquipmentIdentifier from './Cells/EquipmentIdentifier'
import Id from './Cells/Id'
import InvoiceNameCell from './Cells/Name'
import ParentId from './Cells/ParentId'
import QuantityUnitCell from './Cells/QuantityUnit'
import DateFilter from './Filters/Date'
import ProjectFilter from './Filters/Project'
import { mapExpandableInvoiceGroups } from './helper'

export type FilterParams = Partial<{
  projectIds: string
  periodFrom: string
  periodTo: string
}>

export type OverviewTableType = IInvoiceGroup | IInvoice | IInvoiceItem

type Props = TableProps<IInvoice | IInvoiceItem> & {
  defaultFilters?: FilterParams
}
const InvoiceOverviewTable: React.FC<Props> = ({
  extraColumns = [],
  defaultFilters = {},
  ...others
}) => {
  const { params, setSearchParams } = useFilterParams<FilterParams>()

  const { data, loading } = useQuery<IInvoiceGroup[], QlGroupedInvoicesResponseType>(
    INVOICES_GROUPED_BY_MONTH,
    {
      normalizer: (res) => invoiceMonthNormalizer(res),
      variables: { ...params }
    }
  )

  useEffect(() => {
    // set default filter only once, but override query params
    const filtersParams = omitBy<string>({ ...defaultFilters, ...params }, (value) => !value)
    setSearchParams(filtersParams)
  }, [])

  const dataSource = mapExpandableInvoiceGroups(data || [])
  const { tAttribute, tCore } = useTranslation({ recordType: RecordType.invoice, scope: 'table' })
  const { dayjsify } = useDate()
  const [invoice, setInvoice] = useState<IInvoice>(null)

  const closeModal = () => setInvoice(null)

  const columns: ColumnProps<OverviewTableType>[] = [
    {
      title: tAttribute({ id: 'name' }),
      dataIndex: 'name',
      width: 200,
      ellipsis: true,
      render: (_, record) => <InvoiceNameCell member={record} />
    },
    {
      title: tAttribute({ id: 'id' }),
      dataIndex: 'id',
      width: 50,
      ellipsis: true,
      render: (_, record) => <Id member={record as IInvoiceItem} />
    },
    {
      title: tAttribute({ id: 'parent' }),
      dataIndex: 'lineItemId',
      width: 50,
      ellipsis: true,
      render: (_, record) => <ParentId member={record as IInvoiceItem} />
    },
    {
      title: tAttribute({ id: 'equipment_identifier' }),
      dataIndex: 'equipmentIdentifier',
      width: 50,
      ellipsis: true,
      render: (_: string, record) => <EquipmentIdentifier member={record as IInvoiceItem} />
    },
    {
      title: tAttribute({ id: 'invoice_date' }),
      dataIndex: ['financialInfo', 'invoiceDate'],
      width: 50,
      ellipsis: true,
      render: (text) => dayjsify.convertString(text)
    },
    {
      title: tAttribute({ id: 'period_from' }),
      dataIndex: 'periodFrom',
      width: 50,
      render: (text) => dayjsify.convertString(text)
    },
    {
      title: tAttribute({ id: 'period_to' }),
      dataIndex: 'periodTo',
      width: 50,
      render: (text) => dayjsify.convertString(text)
    },
    {
      title: tAttribute({ id: 'quantity' }),
      dataIndex: 'quantity',
      align: 'right',
      width: 50,
      render: (_, record: IInvoiceItem) => <QuantityUnitCell member={record} />
    },
    {
      title: tAttribute({ id: 'unit_price' }),
      dataIndex: 'unitPrice',
      align: 'right',
      width: 50,
      render: (text) => priceFormatter(text)
    },
    {
      title: tAttribute({ id: 'net_total' }),
      dataIndex: 'netTotal',
      align: 'right',
      width: 50,
      render: (text) => priceFormatter(text)
    },

    {
      title: tAttribute({ id: 'gross_total' }),
      dataIndex: 'grossTotal',
      align: 'right',
      width: 50,
      render: (text) => priceFormatter(text)
    },

    {
      title: tAttribute({ id: 'status' }),
      dataIndex: 'status',
      width: 50,
      render: (_: string, record: IInvoice) => <Status invoice={record} />
    },
    {
      title: tCore({ id: 'actions' }),
      width: 50,
      render: (record) => <InvoiceAction member={record} props={{ setInvoice }} />
    }
  ]

  const onProjectFilter = (option: { value: string }) => {
    setSearchParams({
      ...params,
      projectIds: option?.value
    })
  }

  const onDateFilter = (periodFrom: string, periodTo: string) => {
    setSearchParams({ ...params, periodFrom, periodTo })
  }

  return (
    <>
      <KTable<OverviewTableType>
        recordType={RecordType.invoice}
        size="small"
        rowKey={(record: IBaseRecord) => `${record.__typename}-${record.id}`}
        dataSource={dataSource}
        loading={loading}
        columns={[...columns, ...extraColumns]}
        pagination={false}
        tableExtra={<ExportButton filters={params} />}
        empty={{
          default: <KEmpty recordType={RecordType.invoice} id="overview_description" />
        }}
        filters={
          <Space>
            <ProjectFilter onFilter={onProjectFilter} value={params?.projectIds} />
            <DateFilter onFilter={onDateFilter} value={[params?.periodFrom, params?.periodTo]} />
          </Space>
        }
        {...others}
      />
      <InvoiceModal
        member={invoice}
        props={{
          open: !!invoice,
          handleOnClose: closeModal
        }}
      />
    </>
  )
}

export default InvoiceOverviewTable
